import { Loader2, Mic } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { BITS_POR_SEGUNDO, MAX_SEGUNDOS, mimeGrabacion, transcribir } from "@/lib/voz";

type Estado = "listo" | "grabando" | "transcribiendo";

/**
 * Mantener presionado para hablar; al soltar, el texto llega al cuadro para
 * revisarlo antes de enviar (nunca se envía solo). Se corta a los 60 s.
 */
export function Dictado({
  onTexto,
  disabled,
}: {
  onTexto: (texto: string) => void;
  disabled?: boolean;
}) {
  const [estado, setEstado] = useState<Estado>("listo");
  const [segundos, setSegundos] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const grabadora = useRef<MediaRecorder | null>(null);
  const tope = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reloj = useRef<ReturnType<typeof setInterval> | null>(null);
  // Si sueltan antes de que el navegador dé el micrófono, se corta al empezar.
  const presionado = useRef(false);

  const soportado =
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;

  useEffect(
    () => () => {
      if (tope.current) clearTimeout(tope.current);
      if (reloj.current) clearInterval(reloj.current);
      grabadora.current?.stream.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  if (!soportado) return null;

  const empezar = async () => {
    if (estado !== "listo" || disabled) return;
    setError(null);
    const mime = mimeGrabacion((t) => MediaRecorder.isTypeSupported(t));
    if (!mime) {
      setError("Este navegador no puede grabar audio.");
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError("Sin permiso para el micrófono.");
      return;
    }
    const partes: Blob[] = [];
    const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: BITS_POR_SEGUNDO });
    grabadora.current = rec;
    rec.ondataavailable = (e) => e.data.size && partes.push(e.data);
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      if (tope.current) clearTimeout(tope.current);
      if (reloj.current) clearInterval(reloj.current);
      const audio = new Blob(partes, { type: mime });
      partes.length = 0;
      if (audio.size < 200) {
        setEstado("listo");
        return;
      }
      setEstado("transcribiendo");
      try {
        const r = await transcribir(audio, mime);
        if (r.texto.trim()) onTexto(r.texto.trim());
        else setError("No se escuchó nada.");
      } catch (err) {
        setError(err instanceof Error ? err.message : "No pude transcribir el audio.");
      } finally {
        setEstado("listo");
      }
    };
    rec.start();
    setSegundos(0);
    setEstado("grabando");
    reloj.current = setInterval(() => setSegundos((s) => s + 1), 1000);
    tope.current = setTimeout(() => terminar(), MAX_SEGUNDOS * 1000);
    if (!presionado.current) terminar();
  };

  const terminar = () => {
    presionado.current = false;
    const rec = grabadora.current;
    if (rec && rec.state === "recording") rec.stop();
    grabadora.current = null;
  };

  return (
    <div className="flex flex-col items-center">
      <Button
        type="button"
        variant={estado === "grabando" ? "destructive" : "outline"}
        aria-label="Mantén presionado para hablar"
        title="Mantén presionado para hablar"
        disabled={disabled || estado === "transcribiendo"}
        style={{ touchAction: "none" }}
        onPointerDown={(e) => {
          e.preventDefault();
          presionado.current = true;
          void empezar();
        }}
        onPointerUp={terminar}
        onPointerLeave={terminar}
        onContextMenu={(e) => e.preventDefault()}
      >
        {estado === "transcribiendo" ? <Loader2 className="size-4 animate-spin" /> : <Mic className="size-4" />}
      </Button>
      {estado === "grabando" && (
        <span className="text-[10px] text-muted-foreground">
          {segundos}s/{MAX_SEGUNDOS}
        </span>
      )}
      {error && (
        <span role="alert" className="max-w-[120px] text-center text-[10px] text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}
